import { add, addMinutes, getDay, getDaysInMonth, isPast, isSameDay, set } from "date-fns";
import { marginTop, typography } from "../../../styles";
import React, { useMemo } from "react";
import { CalendarDatePickerProps } from "../CalendarDatePicker";
import { DateButton } from "./DateButton/DateButton";
import styled from "styled-components";
import { today } from "../../../../utils";
import { useKeyboardNavigation } from "../useKeyboardNavigation/useKeyboardNavigation";
import { useTranslation } from "react-i18next";
export type DateSelectorProps = Pick<CalendarDatePickerProps, "allowPastSelection" | "availableDates" | "monthOffset" | "onSelectDate" | "selectedDate"> & ReturnType<typeof useKeyboardNavigation>;
const Container = styled.div`
  position: relative;
  z-index: 0;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  justify-items: center;
  ${marginTop.m}
`;
const DayHeader = styled.div`
  ${typography.small}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
export function DateSelector({
  allowPastSelection,
  availableDates,
  focusedDate,
  handleKeyboardNavigation,
  monthOffset,
  onSelectDate,
  selectedDate,
  setFocusedDate
}: DateSelectorProps) {
  const {
    i18n,
    t
  } = useTranslation();
  const locale = i18n.language;

  // Array of two-character day header elements, starting at Monday
  // (using Monday 3 January 2022 as the baseline)
  const dayHeaders = [...Array(7).keys()].map(n => <DayHeader key={n}>
      {new Date(2022, 0, n + 3).toLocaleString(locale, {
      weekday: "short"
    })}
    </DayHeader>);

  /**
   * We don't want to factor in negative time offsets from UTC when calculating these headers,
   * they should only show available dates as returned exactly in the dates from Chronos, rather
   * than accounting for in changes to the day when local time causes them to cross over a date
   * boundry.
   */
  const normalisedDates = useMemo(() => {
    if (!availableDates) {
      return;
    }
    return availableDates.map(date => addMinutes(date, date.getTimezoneOffset()));
  }, [availableDates]);
  const currentMonth = add(today, {
    months: monthOffset
  });
  const numberOfDaysInMonth = getDaysInMonth(currentMonth);
  const firstOfMonthDayOfWeek = getDay(set(currentMonth, {
    date: 1
  }));
  const dateButtons = [...Array(numberOfDaysInMonth).keys()].map(n => {
    const date = set(currentMonth, {
      date: n + 1
    });
    const isFirstDate = n === 0;
    const isToday = isSameDay(today, date);
    const isSelected = selectedDate ? isSameDay(selectedDate, date) : false;
    const isInPast = !isToday && isPast(date);
    const isUnavailable = !allowPastSelection && isInPast || normalisedDates !== undefined && !normalisedDates.some(availableDate => isSameDay(date, availableDate));
    const isFocused = isSameDay(focusedDate, date);
    const dateDisplay = date.toLocaleString(locale, {
      dateStyle: "full"
    });
    const getAriaLabel = () => {
      if (!allowPastSelection && isInPast || isUnavailable) return t("dateIsDisabled", {
        disableDate: dateDisplay
      });
      if (isToday) return t("todayDate", {
        date: dateDisplay
      });
      return dateDisplay;
    };
    const onClick = () => {
      onSelectDate(date);
      setFocusedDate(date);
    };

    /**
     * When a user tabs from the next month button into the list of days
     * we want to make sure that they tab to the first day in the month.
     *
     * However, when the user tabs back from the calendar we do not want
     * them tabbing through the days, we want them to go to the
     * next month button using shift tab from the calendar. We want
     * them to only have to tab once to get back to the next month button.
     *
     * For this we need to make sure that the button has a tab index of 0
     * only when it is the first date of the month and is not currently in focus.
     */
    const firstNotInFocus = focusedDate.getDate() !== 1;
    const tabIndex = isFirstDate && firstNotInFocus ? 0 : -1;
    return <DateButton key={n + 1} {...{
      "aria-label": getAriaLabel(),
      focused: isFocused,
      ...(!isUnavailable && {
        onClick
      }),
      onKeyDown: e => handleKeyboardNavigation(e),
      selected: isSelected,
      tabIndex,
      today: isToday,
      unavailable: isUnavailable,
      // days start at sunday as 0, so if first day is 0, set column to 7
      ...(isFirstDate && {
        column: firstOfMonthDayOfWeek || 7
      })
    }}>
        {n + 1}
      </DateButton>;
  });
  return <Container data-sentry-element="Container" data-sentry-component="DateSelector" data-sentry-source-file="DateSelector.tsx">
      {dayHeaders}
      {dateButtons}
    </Container>;
}
export default DateSelector;